export type FieldType = 'text' | 'number' | 'select' | 'textarea' | 'checkbox' | 'checkboxGroup' | 'radio' | 'date'
export type FieldWidth = 'full' | 'half' | 'third' | 'quarter'
export type SectionLayout = '1-column' | '2-columns' | '3-columns' | '4-columns' | 'auto'

export interface FormField {
  id: string
  label: string
  type: FieldType
  required?: boolean
  enabled: boolean
  order: number
  placeholder?: string
  options?: { value: string; label: string }[]
  defaultValue?: any
  validation?: {
    min?: number
    max?: number
    pattern?: string
    message?: string
  }
  // Layout options
  width?: FieldWidth  // Độ rộng của field (full/half/third/quarter)
  newRow?: boolean    // Bắt đầu dòng mới
}

export interface CheckboxOption {
  id: string
  label: string
  enabled: boolean
  defaultValue?: boolean
}

export interface CheckboxGroupField extends Omit<FormField, 'type' | 'options'> {
  type: 'checkboxGroup'
  options: CheckboxOption[]
}

export interface FormSection {
  id: string
  title: string
  enabled: boolean
  order: number
  color?: string
  layout?: SectionLayout  // Layout của section (1/2/3/4 cột hoặc auto)
  fields: (FormField | CheckboxGroupField)[]
}

export interface CytologyFormSchema {
  id: string
  name: string
  version: string
  sections: FormSection[]
}

// Default schema based on current form
export const DEFAULT_CYTOLOGY_SCHEMA: CytologyFormSchema = {
  id: 'cytology-bethesda-2014',
  name: 'Cytology Report (Bethesda 2014)',
  version: '1.0',
  sections: [
    {
      id: 'patient-info',
      title: 'Thông tin bệnh nhân',
      enabled: true,
      order: 1,
      fields: [
        { id: 'patientName', label: 'Họ và tên', type: 'text', required: true, enabled: true, order: 1 },
        { id: 'age', label: 'Tuổi', type: 'number', required: true, enabled: true, order: 2 },
        { id: 'gender', label: 'Giới tính', type: 'select', required: true, enabled: true, order: 3, options: [
          { value: 'Nam', label: 'Nam' },
          { value: 'Nữ', label: 'Nữ' }
        ]},
        { id: 'patientCode', label: 'Mã bệnh nhân', type: 'text', required: true, enabled: true, order: 4 },
        { id: 'address', label: 'Địa chỉ', type: 'text', required: false, enabled: true, order: 5 },
        { id: 'province', label: 'Tỉnh/Thành phố', type: 'text', required: false, enabled: true, order: 6 },
        { id: 'department', label: 'Khoa', type: 'text', required: false, enabled: true, order: 7 },
        { id: 'treatingDoctor', label: 'Bác sĩ điều trị', type: 'text', required: false, enabled: true, order: 8 },
        { id: 'clinicalDiagnosis', label: 'Chẩn đoán lâm sàng', type: 'textarea', required: false, enabled: true, order: 9 },
        { id: 'specimenType', label: 'Loại mẫu bệnh phẩm', type: 'select', required: true, enabled: true, order: 10, options: [
          { value: 'Phết nhúng dịch', label: 'Phết nhúng dịch' },
          { value: 'Phết thông thường', label: 'Phết thông thường' }
        ]}
      ]
    },
    {
      id: 'organisms',
      title: 'Vi sinh vật (Organisms)',
      enabled: true,
      order: 2,
      color: 'green',
      fields: [
        {
          id: 'organisms',
          label: 'Vi sinh vật',
          type: 'checkboxGroup',
          enabled: true,
          order: 1,
          options: [
            { id: 'trichomonas', label: 'Trichomonas', enabled: true, defaultValue: false },
            { id: 'candida', label: 'Candida', enabled: true, defaultValue: false },
            { id: 'bacterialVaginosis', label: 'Bacterial vaginosis', enabled: true, defaultValue: false },
            { id: 'actinomyces', label: 'Actinomyces', enabled: true, defaultValue: false },
            { id: 'hsv', label: 'HSV', enabled: true, defaultValue: false },
            { id: 'hpv', label: 'HPV', enabled: true, defaultValue: false }
          ]
        }
      ]
    },
    {
      id: 'squamous-cells',
      title: 'Tế bào vảy (Squamous cell)',
      enabled: true,
      order: 3,
      color: 'red',
      fields: [
        {
          id: 'squamousCells',
          label: 'Tế bào vảy',
          type: 'checkboxGroup',
          enabled: true,
          order: 1,
          options: [
            { id: 'ascus', label: 'Tế bào vảy không điển hình không rõ ý nghĩa (ASCUS)', enabled: true, defaultValue: false },
            { id: 'asch', label: 'Tế bào vảy không điển hình không loại trừ tổn thương nội biểu mô độ cao (ASC-H)', enabled: true, defaultValue: false },
            { id: 'lsil', label: 'Tổn thương nội biểu mô vảy độ thấp (LSIL)', enabled: true, defaultValue: false },
            { id: 'lsilHpv', label: 'LSIL/HPV', enabled: true, defaultValue: false },
            { id: 'hsil', label: 'Tổn thương nội biểu mô vảy độ cao (HSIL)', enabled: true, defaultValue: false },
            { id: 'squamousCarcinoma', label: 'Ung thư biểu mô vảy (Squamous carcinoma)', enabled: true, defaultValue: false }
          ]
        }
      ]
    },
    {
      id: 'other-findings',
      title: 'Biến đổi tế bào khác (Other non neoplastic findings)',
      enabled: true,
      order: 4,
      color: 'blue',
      fields: [
        {
          id: 'otherFindings',
          label: 'Biến đổi khác',
          type: 'checkboxGroup',
          enabled: true,
          order: 1,
          options: [
            { id: 'normalCells', label: 'Tế bào vảy và tuyến bình thường', enabled: true, defaultValue: true },
            { id: 'inflammation', label: 'Thay đổi tế bào phản ứng liên quan đến viêm', enabled: true, defaultValue: false },
            { id: 'radiation', label: 'Thay đổi tế bào phản ứng liên quan đến xạ trị', enabled: true, defaultValue: false },
            { id: 'iud', label: 'Thay đổi tế bào phản ứng liên quan đến vòng tránh thai (IUD)', enabled: true, defaultValue: false },
            { id: 'atrophy', label: 'Teo kèm viêm', enabled: true, defaultValue: false },
            { id: 'postHysterectomy', label: 'Tế bào tuyến sau cắt tử cung', enabled: true, defaultValue: false },
            { id: 'endometrialCells', label: 'Tế bào nội mạc tử cung ở phụ nữ ≥45 tuổi', enabled: true, defaultValue: false }
          ]
        }
      ]
    },
    {
      id: 'glandular-cells',
      title: 'Tế bào tuyến (Glandular cell)',
      enabled: true,
      order: 5,
      color: 'yellow',
      fields: [
        {
          id: 'glandularCells',
          label: 'Tế bào tuyến',
          type: 'checkboxGroup',
          enabled: true,
          order: 1,
          options: [
            { id: 'agc', label: 'Tế bào tuyến không điển hình (AGC)', enabled: true, defaultValue: false },
            { id: 'ais', label: 'Ung thư tuyến tại chỗ ống cổ tử cung (AIS)', enabled: true, defaultValue: false },
            { id: 'endocervicalAdenocarcinoma', label: 'Ung thư tuyến ống cổ tử cung', enabled: true, defaultValue: false },
            { id: 'endometrialAdenocarcinoma', label: 'Ung thư tuyến nội mạc tử cung', enabled: true, defaultValue: false },
            { id: 'adenocarcinomaNos', label: 'Ung thư tuyến không đặc hiệu (NOS)', enabled: true, defaultValue: false },
            { id: 'adenocarcinomaExtrauterine', label: 'Ung thư tuyến ngoài tử cung', enabled: true, defaultValue: false }
          ]
        }
      ]
    },
    {
      id: 'conclusion',
      title: 'Kết luận và khuyến cáo',
      enabled: true,
      order: 6,
      fields: [
        { id: 'conclusion', label: 'Kết luận', type: 'textarea', required: true, enabled: true, order: 1 },
        { id: 'recommendation', label: 'Khuyến cáo', type: 'textarea', required: false, enabled: true, order: 2 },
        { id: 'cytopathologist', label: 'Bác sĩ tế bào học', type: 'text', required: false, enabled: true, order: 3 }
      ]
    }
  ]
}
